import React, { useState } from 'react';
import {
  FileCheck, LayoutDashboard,
  CalendarDays, Users, Activity, Settings, Briefcase, Clock3, Layers, Menu
} from 'lucide-react';
import SignOutButton from '../SignOutButton';

// Collapsible (Oct 2026): the hamburger at the top folds the sidebar down to a
// rail of tab icons, each named in its tooltip, to give the roster more room.
// The choice is remembered in this browser only -- a per-person convenience,
// so it is fine for it to be missing (private window, blocked storage).
const STORAGE_KEY = 'sidebarCollapsed';

const readCollapsed = () => {
  try { return window.localStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
};
const saveCollapsed = (value) => {
  try { window.localStorage.setItem(STORAGE_KEY, value ? '1' : '0'); } catch { /* not kept: fine */ }
};

export const MENU_ITEMS = [
  { id: 'final', label: 'Final Roster', icon: FileCheck },
  { id: 'dashboard', label: 'Management Console', icon: LayoutDashboard },
  { id: 'leave', label: 'Leave Roster', icon: CalendarDays },
  { id: 'staff', label: 'Staff List', icon: Users },
  { id: 'rankMaps', label: 'Ranks & Positions', icon: Layers },
  { id: 'skills', label: 'Skills Library', icon: Briefcase },
  { id: 'shiftTypes', label: 'Shift Types', icon: Clock3 },
  { id: 'operations', label: 'Clinical Operations', icon: Activity },
  { id: 'settings', label: 'System Settings', icon: Settings },
];

const Sidebar = ({ activeTab, setActiveTab }) => {
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const toggle = () => setCollapsed(c => { saveCollapsed(!c); return !c; });

  const sidebarBtnClass = (active) => `
    w-full flex items-center ${collapsed ? 'justify-center' : 'gap-3'} p-3 rounded-xl transition-all duration-200 group mb-1
    ${active
      ? 'bg-white text-[#042f2e] font-bold shadow-lg scale-[1.02]'
      : 'text-slate-300 hover:bg-white/5 hover:text-white font-medium'
    }
  `;

  return (
    <div className={`${collapsed ? 'w-[72px]' : 'w-64'} h-full bg-[#042f2e] flex flex-col flex-shrink-0 z-20 shadow-2xl transition-[width] duration-200`}>
      <div className={collapsed ? 'px-3 pt-6 pb-4 flex justify-center' : 'pl-8 pr-4 pt-8 pb-4 mb-4 flex items-start justify-between gap-2'}>
        {!collapsed && (
          <div className="min-w-0">
            <h1 className="text-xl font-bold text-white tracking-tight" style={{ fontFamily: 'var(--font-header)' }}>
              IntelliRoster
            </h1>
            <p className="text-[10px] text-teal-400 font-bold uppercase tracking-[0.2em] opacity-80">
              NTFGH | A DTT Project
            </p>
          </div>
        )}
        <button
          type="button"
          onClick={toggle}
          aria-label={collapsed ? 'Expand the sidebar' : 'Collapse the sidebar'}
          aria-expanded={!collapsed}
          title={collapsed ? 'Expand' : 'Collapse'}
          className="p-2 rounded-lg text-slate-300 hover:bg-white/10 hover:text-white transition-colors flex-shrink-0"
        >
          <Menu className="w-5 h-5" />
        </button>
      </div>

      <nav className={`flex-1 ${collapsed ? 'px-3' : 'px-4'} space-y-1 overflow-y-auto overflow-x-hidden`}>
        {MENU_ITEMS.map((item) => (
          <button
            key={item.id}
            onClick={() => setActiveTab(item.id)}
            className={sidebarBtnClass(activeTab === item.id)}
            title={collapsed ? item.label : undefined}
            aria-label={item.label}
            aria-current={activeTab === item.id ? 'page' : undefined}
          >
            <item.icon className={`w-5 h-5 flex-shrink-0 ${activeTab === item.id ? 'text-[#042f2e]' : 'text-slate-400 group-hover:text-white'}`} />
            {!collapsed && <span className="text-sm whitespace-nowrap">{item.label}</span>}
          </button>
        ))}
      </nav>

      <div className={collapsed ? 'px-3 py-6' : 'p-8'}>
        <div className={`${collapsed ? 'pt-4' : 'pt-6'} border-t border-white/5 space-y-4`}>
          <SignOutButton compact={collapsed} />
          {!collapsed && (
            <p className="text-[10px] text-slate-500 font-medium italic">
              © 2026 IntelliRoster
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
